/**
 * flyToCart — grabs a snapshot of the product card image (already loaded
 * in the DOM) and animates it flying into the cart icon with a bezier arc.
 *
 * @param {HTMLElement|null} sourceEl  - the img or placeholder div element
 * @param {string|null}      imageUrl  - fallback src (used only if sourceEl has no loaded img)
 */
export function flyToCart(sourceEl, imageUrl, fallbackEmoji = '🍾') {
  const cartEl = document.getElementById('header-cart-icon')
  if (!cartEl) return

  // ── Find the best source element ─────────────────────────────────
  // Walk up/down from sourceEl to find an actual loaded <img>
  let imgEl = null
  if (sourceEl) {
    if (sourceEl.tagName === 'IMG' && sourceEl.complete && sourceEl.naturalWidth > 0) {
      imgEl = sourceEl
    } else {
      // Maybe sourceEl is a container — look for img inside it
      imgEl = sourceEl.querySelector('img[complete]') ||
               sourceEl.querySelector('img')
      if (imgEl && (!imgEl.complete || imgEl.naturalWidth === 0)) imgEl = null
    }
    // Walk up to the card and find the img from there
    if (!imgEl) {
      const card = sourceEl.closest('a.card, .card')
      if (card) {
        const found = card.querySelector('img')
        if (found && found.complete && found.naturalWidth > 0) imgEl = found
      }
    }
  }

  // ── Build source rect ─────────────────────────────────────────────
  const refEl = imgEl || sourceEl
  const srcRect = refEl
    ? refEl.getBoundingClientRect()
    : { left: window.innerWidth / 2 - 26, top: window.innerHeight / 2 - 26, width: 52, height: 52 }

  const dstRect = cartEl.getBoundingClientRect()
  const dstCX = dstRect.left + dstRect.width / 2
  const dstCY = dstRect.top + dstRect.height / 2

  const SIZE = 56
  const startX = srcRect.left + srcRect.width / 2 - SIZE / 2
  const startY = srcRect.top + srcRect.height / 2 - SIZE / 2

  // ── Build the clone ───────────────────────────────────────────────
  const clone = document.createElement('div')
  clone.style.cssText = `
    position: fixed;
    z-index: 9999;
    pointer-events: none;
    border-radius: 50%;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(0,0,0,0.30);
    width: ${SIZE}px;
    height: ${SIZE}px;
    left: ${startX}px;
    top: ${startY}px;
    will-change: transform, opacity;
    background: white;
  `

  if (imgEl) {
    // ✅ Clone the already-rendered image — instant, no reload
    const cloneImg = imgEl.cloneNode(false)
    cloneImg.style.cssText = 'width:100%;height:100%;object-fit:contain;padding:4px;'
    clone.appendChild(cloneImg)
  } else if (imageUrl) {
    // Fallback: create img from URL — use a canvas trick to paint synchronously
    const canvas = document.createElement('canvas')
    canvas.width = SIZE
    canvas.height = SIZE
    const ctx = canvas.getContext('2d')

    // Try drawing from a cached image if browser has it
    const tempImg = new Image()
    tempImg.crossOrigin = 'anonymous'
    tempImg.src = imageUrl

    if (tempImg.complete && tempImg.naturalWidth > 0) {
      ctx.drawImage(tempImg, 4, 4, SIZE - 8, SIZE - 8)
      clone.appendChild(canvas)
    } else {
      // Not cached — use emoji placeholder, looks better than blank white
      clone.style.background = 'linear-gradient(135deg, #0e947a18, #d4af3730)'
      clone.style.fontSize = '28px'
      clone.style.display = 'flex'
      clone.style.alignItems = 'center'
      clone.style.justifyContent = 'center'
      clone.textContent = fallbackEmoji
    }
  } else {
    clone.style.background = 'linear-gradient(135deg, #0e947a18, #d4af3730)'
    clone.style.fontSize = '28px'
    clone.style.display = 'flex'
    clone.style.alignItems = 'center'
    clone.style.justifyContent = 'center'
    clone.textContent = fallbackEmoji
  }

  document.body.appendChild(clone)

  // ── Animate along a bezier arc ────────────────────────────────────
  const endX = dstCX - SIZE / 2
  const endY = dstCY - SIZE / 2
  const dx = endX - startX
  const dy = endY - startY
  const dist = Math.sqrt(dx * dx + dy * dy)
  const duration = Math.min(700, Math.max(380, dist * 0.55))

  // Control point: arc upward and toward the cart
  const cpX = startX + dx * 0.4
  const cpY = startY - Math.abs(dx) * 0.35

  let start = null

  function easeInOut(t) {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
  }

  function step(ts) {
    if (!start) start = ts
    const raw = Math.min((ts - start) / duration, 1)
    const t = easeInOut(raw)

    // Quadratic bezier
    const bx = (1 - t) * (1 - t) * startX + 2 * (1 - t) * t * cpX + t * t * endX
    const by = (1 - t) * (1 - t) * startY + 2 * (1 - t) * t * cpY + t * t * endY

    const scale = 1 - t * 0.72         // 1.0 → 0.28
    const opacity = raw > 0.80 ? 1 - (raw - 0.80) / 0.20 : 1

    clone.style.transform = `translate(${bx - startX}px, ${by - startY}px) scale(${scale})`
    clone.style.opacity = opacity

    if (raw < 1) {
      requestAnimationFrame(step)
    } else {
      clone.remove()
      wiggleEl(cartEl, 'cart')
      const bellEl = document.getElementById('header-bell-icon')
      if (bellEl) wiggleEl(bellEl, 'bell')
    }
  }

  requestAnimationFrame(step)
}

function wiggleEl(el, type) {
  el.classList.remove(`${type}-wiggle`)
  void el.offsetWidth  // force reflow
  el.classList.add(`${type}-wiggle`)
  el.addEventListener('animationend', () => el.classList.remove(`${type}-wiggle`), { once: true })
}
